import React, { useState, useRef, useEffect } from 'react';
import { FaTag, FaTrash, FaUser } from 'react-icons/fa';

const ArtworkTagger = ({ 
  artworkUrl, 
  onCharactersTagged, 
  initialCharacters = [],
  initialTags = [],
  onClose,
  storyId = null,
  currentUser = null,
  onRefineArtwork // Added onRefineArtwork prop
}) => {
  console.log('🎨 ArtworkTagger loaded - Version 4.0 (Backend Processing)');
  
  const [peopleTags, setPeopleTags] = useState(initialTags.filter(t => t.type === 'people'));
  const [objectTags, setObjectTags] = useState(initialTags.filter(t => t.type === 'object'));
  const [selectedTag, setSelectedTag] = useState(null);
  const [draggingTag, setDraggingTag] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [artworkType, setArtworkType] = useState('people');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showHoverTags, setShowHoverTags] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form states for each type
  const [peopleDraft, setPeopleDraft] = useState({ name: '', age: '', gender: '', appearance: '' });
  const [objectDraft, setObjectDraft] = useState({ objectName: '', objectBrief: '' });
  
  // Debounced values to prevent excessive tag generation
  const [debouncedPeopleName, setDebouncedPeopleName] = useState('');
  const [debouncedObjectName, setDebouncedObjectName] = useState('');
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Predefined tag positions
  const tagPositions = [
    { x: 30, y: 30 },
    { x: 70, y: 30 },
    { x: 30, y: 70 },
    { x: 70, y: 70 },
    { x: 50, y: 50 },
    { x: 35, y: 65 },
    { x: 65, y: 35 },
    { x: 65, y: 65 },
    { x: 35, y: 35 },
  ];

  // Initialize tags from initialTags prop
  useEffect(() => {
    setPeopleTags(initialTags.filter(t => t.type === 'people'));
    setObjectTags(initialTags.filter(t => t.type === 'object'));
  }, [initialTags]);

  // Handler to add a new people tag only when user confirms (Enter, blur, or Add button)
  const handleAddPeopleTag = () => {
    if (artworkType !== 'people' || !peopleDraft.name.trim()) return;
    const existingTag = peopleTags.find(tag => tag.name === peopleDraft.name.trim());
    if (!existingTag) {
      const positionIndex = peopleTags.length % tagPositions.length;
      const newTag = {
        id: `tag_${Date.now()}`,
        type: 'people',
        name: peopleDraft.name.trim(),
        age: peopleDraft.age,
        gender: peopleDraft.gender,
        appearance: peopleDraft.appearance,
        x: tagPositions[positionIndex].x,
        y: tagPositions[positionIndex].y,
        isPlaced: true
      };
      setPeopleTags(prev => [...prev, newTag]);
      setPeopleDraft({ name: '', age: '', gender: '', appearance: '' });
    } else {
      // Update existing tag
      setPeopleTags(prev => prev.map(tag => 
        tag.name === peopleDraft.name.trim() 
          ? { ...tag, age: peopleDraft.age, gender: peopleDraft.gender, appearance: peopleDraft.appearance }
          : tag
      ));
      setPeopleDraft({ name: '', age: '', gender: '', appearance: '' });
    }
  };

  // Debounce object name input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedObjectName(objectDraft.objectName);
    }, 500);

    return () => clearTimeout(timer);
  }, [objectDraft.objectName]);

  // Auto-generate tags when debounced object name changes
  useEffect(() => {
    if (artworkType === 'object' && debouncedObjectName.trim()) {
      setObjectTags(prev => {
        const existingTag = prev.find(tag => tag.objectName === debouncedObjectName);
        if (!existingTag) {
          const newTag = {
            id: `tag_${Date.now()}`,
            type: 'object',
            objectName: debouncedObjectName,
            objectBrief: objectDraft.objectBrief,
            x: 50,
            y: 50,
            isPlaced: true 
          };
          return [...prev, newTag];
        } else {
          // Update existing tag
          return prev.map(tag => 
            tag.objectName === debouncedObjectName 
              ? { ...tag, objectBrief: objectDraft.objectBrief }
              : tag
          );
        }
      });
    }
  }, [debouncedObjectName, artworkType, objectDraft.objectBrief]);

  const handleTagClick = (tagId) => {
    setSelectedTag(tagId);
  };

  // Mouse event handlers for dragging
  const handleMouseDown = (e, tagId) => {
    e.stopPropagation();
    const rect = imageRef.current.getBoundingClientRect();
    const allTags = [...peopleTags, ...objectTags];
    const tag = allTags.find(t => t.id === tagId);
    if (!tag) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const tagX = (tag.x / 100) * rect.width;
    const tagY = (tag.y / 100) * rect.height;

    setDragOffset({
      x: mouseX - tagX,
      y: mouseY - tagY
    });
    setDraggingTag(tagId);
    setSelectedTag(tagId);
  };

  const handleMouseMove = (e) => {
    if (!draggingTag || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100;
    const y = ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100;

    const allTags = [...peopleTags, ...objectTags];
    const tag = allTags.find(t => t.id === draggingTag);
    if (!tag) return;

    const updatedTag = { ...tag, x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
    
    if (tag.type === 'people') {
      setPeopleTags(prev => prev.map(t => t.id === draggingTag ? updatedTag : t));
    } else if (tag.type === 'object') {
      setObjectTags(prev => prev.map(t => t.id === draggingTag ? updatedTag : t));
    }
  };

  const handleMouseUp = () => {
    setDraggingTag(null);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleDeleteTag = (tagId) => {
    setPeopleTags(prev => prev.filter(tag => tag.id !== tagId));
    setObjectTags(prev => prev.filter(tag => tag.id !== tagId));
    if (selectedTag === tagId) {
      setSelectedTag(null);
    }
  };

  const handleSaveTags = async () => {
    const allTags = [...peopleTags, ...objectTags];
    console.log('=== SAVING TAGS TO BACKEND ===');
    console.log('All tags to save:', allTags);
    
    if (allTags.length === 0) {
      console.warn('No tags to save!');
      alert('Please add at least one tag before saving.');
      return;
    }

    setIsSaving(true);
    
    try {
      // Handle blob URLs by converting to base64
      let processedArtworkUrl = artworkUrl;
      
      if (artworkUrl && artworkUrl.startsWith('blob:')) {
        console.log('Converting blob URL to base64 for backend processing...');
        try {
          const response = await fetch(artworkUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          
          processedArtworkUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          console.log('Successfully converted blob URL to base64');
        } catch (error) {
          console.error('Failed to convert blob URL to base64:', error);
          throw new Error('Failed to process artwork image for backend');
        }
      }

      // Prepare data for backend processing
      const tagData = {
        storyId: storyId,
        userId: currentUser?.uid,
        artworkUrl: processedArtworkUrl,
        tags: allTags,
        timestamp: new Date().toISOString()
      };

      console.log('Sending tag data to backend:', {
        ...tagData,
        artworkUrl: processedArtworkUrl.substring(0, 100) + '...'
      });

      // Call the parent's callback with the tag data
      // The parent component should handle sending this to the backend
      onCharactersTagged(allTags, tagData);
      
      console.log('✅ Tags sent to backend for processing');
      onClose();
      
    } catch (error) {
      console.error('Error saving tags:', error);
      alert(`Failed to save tags: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageLoad = () => {
    console.log('=== IMAGE LOADED ===');
    console.log('Image ref:', imageRef.current);
    console.log('Image natural dimensions:', {
      width: imageRef.current?.naturalWidth,
      height: imageRef.current?.naturalHeight
    });
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    console.error('=== IMAGE LOAD ERROR ===');
    setImageError(true);
    setImageLoaded(false);
  };

  // Helper to get label for tag
  const getTagLabel = (tag) => {
    if (tag.type === 'people') return tag.name || '';
    if (tag.type === 'object') return tag.objectName || '';
    return '';
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (draggingTag) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingTag, dragOffset, handleMouseMove]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-amber-200">
          <h2 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
            <FaTag />
            🎨 Tag Characters on Artwork (Backend Processing v4.0)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-full overflow-y-auto max-h-[80vh]">
          {/* Left: Artwork with tags */}
          <div className="lg:w-2/3 p-6 overflow-y-auto max-h-[80vh]">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">
                🎨 Tags appear automatically as you type!
              </h3>
              <p className="text-sm text-amber-600">
                Add character details on the right, and tags will automatically appear on your artwork. Drag tags to position them!
              </p>
              <div className="mt-2">
                <label className="flex items-center gap-2 text-sm text-amber-700">
                  <input
                    type="checkbox"
                    checked={showHoverTags}
                    onChange={(e) => setShowHoverTags(e.target.checked)}
                    className="rounded"
                  />
                  Show tag labels on artwork
                </label>
              </div>
            </div>

            {/* Artwork Container */}
            <div 
              ref={containerRef}
              className="relative bg-amber-50 border-2 border-amber-200 rounded-xl overflow-hidden mb-4"
              style={{ aspectRatio: '1', maxHeight: '80vh' }}
            >
              {imageError ? (
                <div className="w-full h-full flex items-center justify-center text-amber-600">
                  <div className="text-center">
                    <FaUser className="text-4xl mx-auto mb-2 opacity-50" />
                    <p>Failed to load artwork</p>
                  </div>
                </div>
              ) : (
                <>
                  <img
                    ref={imageRef}
                    src={artworkUrl}
                    alt="Artwork for tagging"
                    className={`w-full h-full object-contain cursor-default`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: imageLoaded ? 'block' : 'none' }}
                  />
                  
                  {/* Loading state */}
                  {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                    </div>
                  )}

                  {/* Tags overlay - show ALL tags regardless of selected type */}
                  {[...peopleTags, ...objectTags].map(tag => (
                    <div
                      key={tag.id}
                      className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-move transition-all duration-200 ${
                        selectedTag === tag.id ? 'z-20' : 'z-10'
                      } ${draggingTag === tag.id ? 'z-30' : ''}`}
                      style={{
                        left: `${tag.x}%`,
                        top: `${tag.y}%`
                      }}
                      onMouseDown={(e) => handleMouseDown(e, tag.id)}
                      onClick={() => handleTagClick(tag.id)}
                    >
                      {/* Tag marker */}
                      <div className={`
                        w-8 h-8 rounded-full border-2 flex items-center justify-center text-white font-bold text-sm shadow-lg
                        ${selectedTag === tag.id 
                          ? 'bg-amber-500 border-amber-700 scale-110' 
                          : tag.type === 'people' ? 'bg-amber-400 border-amber-600' :
                            tag.type === 'object' ? 'bg-amber-500 border-amber-700' :
                            'bg-amber-600 border-amber-800'
                        }
                        ${draggingTag === tag.id ? 'scale-125 shadow-xl' : ''}
                      `}>
                        {getTagLabel(tag).charAt(0).toUpperCase()}
                      </div>
                      {/* Tag label */}
                      <div className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-white border border-amber-200 rounded-lg px-2 py-1 text-xs font-semibold text-amber-800 shadow-md whitespace-nowrap">
                        {getTagLabel(tag)}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Refine Artwork button */}
            {artworkUrl && (
              <button
                type="button"
                className="w-full bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-600 hover:to-purple-500 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
                onClick={() => {
                  // Call the parent's refine artwork function if provided
                  if (onRefineArtwork) {
                    onRefineArtwork(artworkUrl, [...peopleTags, ...objectTags]);
                  } else {
                    // Fallback: close the tagger and let parent handle it
                    onClose();
                  }
                }}
              >
                ✨ Refine Artwork
                </button>
              )}
          </div>

          {/* Right: Character form and tag list */}
          <div className="lg:w-1/3 p-6 bg-amber-50 border-l border-amber-200 flex flex-col h-full overflow-y-auto max-h-[80vh]">
            {/* Tag type selection */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <FaUser />
                What do you want to tag?
              </h3>
              
              {/* Summary section */}
              {[...peopleTags, ...objectTags].length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="text-sm text-amber-800">
                    <div className="font-semibold mb-1">📊 Tag Summary:</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>Total tags: {[...peopleTags, ...objectTags].length}</div>
                      <div>Characters: {peopleTags.length}</div>
                      <div>Scene Items: {objectTags.length}</div>
                    </div>
                    {isSaving && (
                      <div className="text-green-600 mt-2 flex items-center gap-1">
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-green-600"></div>
                        Processing tags in backend...
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="artworkType"
                    value="people"
                    checked={artworkType === 'people'}
                    onChange={() => setArtworkType('people')}
                  />
                  Characters
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="artworkType"
                    value="object"
                    checked={artworkType === 'object'}
                    onChange={() => setArtworkType('object')}
                  />
                  Scene Items
                </label>
              </div>
            </div>

            {/* Type-specific forms */}
            {artworkType === 'people' && (
              <div className="space-y-3 mb-4">
                <div className="text-amber-700 font-semibold mb-2">
                  🧑‍🎨 Add character details - tags appear automatically!
                </div>
                {peopleDraft.name.trim() && !debouncedPeopleName && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
                    ⏳ Preparing tag for "{peopleDraft.name}"...
                  </div>
                )}
                <input
                  type="text"
                  value={peopleDraft.name}
                  onChange={e => setPeopleDraft(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddPeopleTag(); }}
                  onBlur={handleAddPeopleTag}
                  placeholder="Character name"
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                />
                <input
                  type="number"
                  value={peopleDraft.age}
                  onChange={e => setPeopleDraft(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="Age (optional)"
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                />
                <select
                  value={peopleDraft.gender}
                  onChange={e => setPeopleDraft(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                >
                  <option value="">Gender (optional)</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                </select>
                <textarea
                  value={peopleDraft.appearance}
                  onChange={e => setPeopleDraft(prev => ({ ...prev, appearance: e.target.value }))}
                  placeholder="Clothes & appearance (optional)"
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  rows="2"
                />
              </div>
            )}

            {artworkType === 'object' && (
              <div className="space-y-3 mb-4">
                <div className="text-amber-700 font-semibold mb-2">
                  🎯 Add scene item details - tags appear automatically!
                </div>
                {objectDraft.objectName.trim() && !debouncedObjectName && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg border border-amber-200">
                    ⏳ Preparing tag for "{objectDraft.objectName}"...
                  </div>
                )}
                <input
                  type="text"
                  value={objectDraft.objectName}
                  onChange={e => setObjectDraft(prev => ({ ...prev, objectName: e.target.value }))}
                  placeholder="Item name (e.g., cat, tree, toy)"
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                />
                <textarea
                  value={objectDraft.objectBrief}
                  onChange={e => setObjectDraft(prev => ({ ...prev, objectBrief: e.target.value }))}
                  placeholder="Brief description"
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  rows="2"
                />
            </div>
            )}

            {/* Display all existing tags */}
            <div className="mb-4">
              <h4 className="text-md font-semibold text-amber-800 mb-2">
                All Tags on Artwork ({[...peopleTags, ...objectTags].length})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[...peopleTags, ...objectTags].length === 0 ? (
                  <p className="text-sm text-amber-600">No tags added yet. Start typing above to add tags!</p>
                ) : (
                  [...peopleTags, ...objectTags].map(tag => (
                    <div key={tag.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg p-2">
                      <div className="flex items-center gap-2 flex-1">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-white font-bold text-xs ${
                          tag.type === 'people' ? 'bg-amber-400 border-amber-600' :
                          tag.type === 'object' ? 'bg-amber-500 border-amber-700' :
                          'bg-amber-600 border-amber-800'
                        }`}>
                          {getTagLabel(tag).charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-amber-800 block truncate">{getTagLabel(tag)}</span>
                          <span className="text-xs text-amber-600">({tag.type})</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTag(tag.id)}
                        className="text-red-500 hover:text-red-700 text-sm ml-2"
                        title="Delete tag"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Existing character dropdown */}
            <div className="mb-4">
              {initialCharacters && initialCharacters.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-amber-800 mb-2">
                    Select Existing Character (Optional)
                  </label>
                  <select
                    onChange={e => {
                      const selectedChar = initialCharacters.find(char => char.name === e.target.value);
                      if (selectedChar) {
                        setArtworkType('people');
                        setPeopleDraft({
                          name: selectedChar.name || '',
                          age: selectedChar.age || '',
                          gender: selectedChar.gender || '',
                          appearance: selectedChar.appearance || ''
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  >
                    <option value="">-- Select an existing character --</option>
                    {initialCharacters.map((char, index) => (
                      <option key={index} value={char.name}>
                        {char.name} {char.age ? `(${char.age}${char.gender ? `, ${char.gender}` : ''})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-amber-600 mt-1">
                    Select a character to pre-fill the form
                  </p>
                </div>
              )}
            </div>

            {/* Done button */}
            <div className="flex justify-end mt-8 mb-2">
              <button
                onClick={handleSaveTags}
                disabled={isSaving}
                className={`font-semibold py-3 px-8 rounded-xl shadow-lg text-lg transition-all duration-300 ${
                  isSaving 
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-green-500 to-green-400 hover:from-green-600 hover:to-green-500 text-white'
                }`}
              >
                {isSaving ? 'Processing...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkTagger; 